import React, { useCallback, useEffect, useState } from 'react';
import { TextProps } from '@ydistri/ds';
import AsoTooltip, { AsoTooltipKey } from '../../AsoTooltip';
import {
  InputNumber100Px,
  LabelFieldWrapper,
  SliderField,
  SliderInputWrapper,
  TextWithTooltip,
} from './asoSettingsStyledComponents';

interface AsoSliderInputRowProps {
  label: string;
  labelWidth?: TextProps['$width'];
  value: number | null | undefined;
  onChange: (value: number | null) => void;
  min: number;
  max: number;
  step: number;
  precision?: number;
  tooltipKey?: AsoTooltipKey;
}

export const AsoSliderInputRow: React.FC<AsoSliderInputRowProps> = ({
  label,
  labelWidth = '15rem',
  value,
  onChange,
  min,
  max,
  step,
  precision = 2,
  tooltipKey,
}) => {
  const normalizeValue = useCallback(
    (nextValue: number | null | undefined) => {
      if (typeof nextValue !== 'number' || !Number.isFinite(nextValue)) return null;

      const boundedValue = Math.min(max, Math.max(min, nextValue));
      const steppedValue = min + Math.round((boundedValue - min) / step) * step;
      const normalizedValue = Math.min(max, Math.max(min, steppedValue));

      return Number(normalizedValue.toFixed(precision));
    },
    [max, min, precision, step],
  );

  const [val, setVal] = useState<number>(() => normalizeValue(value) ?? min);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- needed
    setVal(normalizeValue(value) ?? min);
  }, [min, normalizeValue, value]);

  const handleValueChange = useCallback(
    (nextValue: number | null) => {
      const newValue = normalizeValue(nextValue) ?? min;
      onChange(newValue);
      setVal(newValue);
    },
    [min, normalizeValue, onChange],
  );

  return (
    <LabelFieldWrapper>
      <TextWithTooltip as="span" $width={labelWidth}>
        {label}
        {tooltipKey && <AsoTooltip tooltipKey={tooltipKey} />}
      </TextWithTooltip>
      <SliderInputWrapper>
        <SliderField min={min} max={max} step={step} value={val} onChange={handleValueChange} />
        <InputNumber100Px
          precision={precision}
          min={min}
          max={max}
          step={step}
          value={val}
          onChange={nextValue => {
            handleValueChange(typeof nextValue === 'number' ? nextValue : null);
          }}
        />
      </SliderInputWrapper>
    </LabelFieldWrapper>
  );
};
